import { DEFAULT_QUOTE_LANGUAGE, QUOTE_LANGUAGES } from "../constants/languages";

// Mirrors the backend's utils/language.js detectLanguage - keep both in sync.
// Detects a text's language from its script: Tamil letters -> "ta", Latin
// letters -> "en", etc. Devanagari is reported as "hi", Arabic script as "ur".
const SCRIPT_RANGES: [string, number, number][] = [
  ["ta", 0x0b80, 0x0bff], // Tamil
  ["hi", 0x0900, 0x097f], // Devanagari (Hindi / Marathi)
  ["bn", 0x0980, 0x09ff], // Bengali
  ["pa", 0x0a00, 0x0a7f], // Gurmukhi (Punjabi)
  ["gu", 0x0a80, 0x0aff], // Gujarati
  ["te", 0x0c00, 0x0c7f], // Telugu
  ["kn", 0x0c80, 0x0cff], // Kannada
  ["ml", 0x0d00, 0x0d7f], // Malayalam
  ["ur", 0x0600, 0x06ff], // Arabic script (Urdu)
];

export function detectLanguage(text: string | null | undefined): string {
  const counts: Record<string, number> = { [DEFAULT_QUOTE_LANGUAGE]: 0 };
  for (const char of String(text || "")) {
    const cp = char.codePointAt(0) ?? 0;
    if ((cp >= 0x41 && cp <= 0x5a) || (cp >= 0x61 && cp <= 0x7a)) {
      counts[DEFAULT_QUOTE_LANGUAGE] += 1;
      continue;
    }
    const match = SCRIPT_RANGES.find(([, start, end]) => cp >= start && cp <= end);
    if (match) counts[match[0]] = (counts[match[0]] || 0) + 1;
  }

  let best = DEFAULT_QUOTE_LANGUAGE;
  for (const [code, count] of Object.entries(counts)) {
    if (count > counts[best]) best = code;
  }
  return best;
}

// The language a quote was originally written in: the backend's stored
// `language` when present (quotes.language), otherwise detected from the
// text (e.g. for a just-created post that hasn't been refetched yet).
export function sourceLanguageOf(item: { language?: string | null; title?: string | null; content?: string | null }): string {
  const stored = normalizeLanguageCode(item.language);
  if (stored) return stored;
  return detectLanguage(item.title ?? item.content ?? "");
}

const LATIN_SCRIPT_LANGUAGES = new Set(["en", "es", "fr", "de", "pt", "it"]);
const SAME_SCRIPT_AS: Record<string, string> = { mr: "hi", ar: "ur" };

// What detectLanguage() should return for correct text in `code`, or null
// when the detector can't tell (e.g. Chinese, Japanese, Russian).
export function expectedDetectionFor(code: string): string | null {
  if (LATIN_SCRIPT_LANGUAGES.has(code)) return DEFAULT_QUOTE_LANGUAGE;
  if (SAME_SCRIPT_AS[code]) return SAME_SCRIPT_AS[code];
  return SCRIPT_RANGES.some(([script]) => script === code) ? code : null;
}

export function languageLabel(code: string): string {
  return QUOTE_LANGUAGES.find((lang) => lang.code === code)?.label ?? code;
}

export function speechLocaleFor(code: string): string {
  // Languages without a known voice locale use their code; the browser picks
  // a matching voice if it has one.
  return QUOTE_LANGUAGES.find((lang) => lang.code === code)?.speechLocale ?? code;
}

// Standard codes that Google Translate spells differently.
const LANGUAGE_ALIASES: Record<string, string> = {
  he: "iw",
  jv: "jw",
  zh: "zh-CN",
  fil: "tl",
  nb: "no",
  "pt-br": "pt",
};

// Maps any casing/alias of a supported code ("ZH-cn", "he") to the exact
// code in QUOTE_LANGUAGES ("zh-CN", "iw"); null if unsupported.
export function normalizeLanguageCode(code: string | null | undefined): string | null {
  const value = (code || "").trim().toLowerCase();
  if (!value) return null;
  const target = LANGUAGE_ALIASES[value] ?? value;
  return QUOTE_LANGUAGES.find((lang) => lang.code.toLowerCase() === target.toLowerCase())?.code ?? null;
}
